import { DropdownItem, DropdownOptions } from '@/components/Dropdown';
import { Location, SortDirectionEnum } from '@/models/gen/graphql';
import QueryDropdown, { QueryDropdownProps } from '../QueryDropdown';
import { QueryInput, QueryInputType, queryInput } from '@/utils';
import { ReactNode, forwardRef, useCallback, useEffect, useRef } from 'react';

import { Button } from 'react-bootstrap';
import Tippy from '@tippyjs/react';
import { fieldWrapper } from '@/components/Field';
import { selectLocationFilter } from '@/api/services/locations/searchLocations';
import { useGlobalEditLocationModalState } from '@/components/GlobalEditLocationModal';

export type LocationQueryDropdownProps = Omit<QueryDropdownProps, 'query'> & {
  airports?: string[];
  query?: Record<string, QueryInput>[];
  options?: LocationQueryDropdownOptions;
  onCreate?: (location: Location) => void;
  // Only shows active locations. If the selected location is inactive it's still shown (disabled) so existing values aren't cleared.
  filterActiveLocations?: boolean;
};

export type LocationQueryDropdownOptions = DropdownOptions & {
  showAirportPrefix?: boolean;
  autoSelectLocation?: string;
};

const LocationQueryDropdown = (() => {
  const LocationQueryDropdown = (
    {
      name = 'location',
      airports = [],
      query,
      options: allOptions,
      onCreate,
      filterActiveLocations,
      ...queryDropdownProps
    }: LocationQueryDropdownProps,
    ref
  ): ReactNode => {
    const showEditLocationModal = useGlobalEditLocationModalState(({ setState }) => setState);
    const isDirty = useRef<boolean>(false);
    const lastItems = useRef<DropdownItem[]>(null);
    const { autoSelectLocation, showAirportPrefix, ...queryDropdownOptions } = allOptions || {};
    const isFiltered = filterActiveLocations !== undefined && filterActiveLocations !== false;
    // Only refetch on value changes when filtering, since the selected location is included in the query.
    const selectedLocationId = isFiltered ? (queryDropdownProps?.value as string) : undefined;

    const handleCreateLocation = (): void => {
      showEditLocationModal({
        show: true,
        data: undefined,
        onSubmit: undefined,
        onAfterSubmit: async (_update, newLocation) => {
          await ref.current?.refreshItems();
          onCreate?.(newLocation);
        },
        onHide: undefined,
      });
    };

    const handleChange = (value: string): void => {
      isDirty.current = true;
      queryDropdownProps.onChange?.(value, undefined);
    };

    const handleAutoSelect = (items = []) => {
      if (!autoSelectLocation || !!queryDropdownProps?.value) return;
      const option = items.find((item) => !item.disabled && item.label.trim() === autoSelectLocation);
      if (!option || option.value === queryDropdownProps?.value) return;
      queryDropdownProps?.onChange?.(option.value, undefined);
    };

    const getItems = useCallback(async (): Promise<DropdownItem[]> => {
      const res = await selectLocationFilter(
        query || [
          {
            airportCode: airports?.length > 0 ? queryInput(airports, QueryInputType.OR, SortDirectionEnum.Asc) : null,
            name: queryInput([], QueryInputType.DEFAULT, SortDirectionEnum.Asc),
          },
        ],
        { filterActiveLocations: isFiltered, selectedLocationId }
      );
      const items = (res?.rows || []).map(
        ({ id, name, displayName, active }: Location): DropdownItem => ({
          label: showAirportPrefix !== false ? displayName : name,
          keywords: name.split(' '),
          value: id,
          disabled: isFiltered && active !== 1,
        })
      );
      lastItems.current = items;
      handleAutoSelect(items);
      return items;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, airports, showAirportPrefix, isFiltered, selectedLocationId]);

    useEffect(() => {
      if (!autoSelectLocation) return;
      getItems();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSelectLocation]);

    return (
      <QueryDropdown
        {...queryDropdownProps}
        onChange={handleChange}
        ref={ref}
        options={{
          ...queryDropdownOptions,
          indicators: (
            <>
              {onCreate && (
                <Tippy content="Create New Location">
                  <Button variant="icon" onClick={handleCreateLocation} tabIndex={-1}>
                    <i className="fa fa-plus" />
                  </Button>
                </Tippy>
              )}
              {queryDropdownOptions?.indicators || null}
            </>
          ),
        }}
        name={name}
        query={getItems}
      />
    );
  };
  return forwardRef(LocationQueryDropdown);
})();

export const LocationQueryDropdownField = fieldWrapper<LocationQueryDropdownProps>(LocationQueryDropdown);

export default LocationQueryDropdown;
